// /src/services/niji.ts
const BASE_URL = import.meta.env.VITE_DATA_URL;
// const BASE_URL = "http://localhost:3220";
const headers = {
    'Content-Type': 'application/json',
    'Authorization': import.meta.env.VITE_API_ORBBYTE_KEY
};

import { saveImageToAppStorage } from '@/utils/image';
import { logEvent } from '@/utils/logs';
import axios from 'axios';
import OpenAI from 'openai';
import { GoogleGenerativeAI } from "@google/generative-ai";
import { safeJSONParser } from '@/utils/format';
import { getLanguageLabel, getUserLanguage } from '@/utils/lang';
import appStore from '@/utils/store';
import { CalooAdminConfig } from '../admin';
import { incrementTokens, incrementTokensText } from '../tokens';
import { buildPromptWithLocation } from '../location';

const OPENAI_API_KEY_ = import.meta.env.VITE_OPENAI_API_KEY;
const GEMINI_API_KEY_ = import.meta.env.VITE_GEMINI_API_KEY

const openai = new OpenAI({
    apiKey: OPENAI_API_KEY_,
    dangerouslyAllowBrowser: true,
});

const genAI = new GoogleGenerativeAI(GEMINI_API_KEY_);


export function getDefaultPrompt(language: string, type = 'image'): string {
    try {
        const prompt = type === 'image'
            ? CalooAdminConfig.defaultImagePrompt.replace("${language}", language)
            : type === 'audio' ? CalooAdminConfig.defaultAudioPrompt.replace("${language}", language) : CalooAdminConfig.defaultEditPrompt.replace("${language}", language);
        if (prompt) {
            console.log("✅ Usando prompt personalizado:", prompt);
            return prompt;
        }
    } catch (error) {
        logEvent(`❌ Error al obtener el prompt personalizado: ${error}`);
    }
    return `[Analizador nutricional: estimación exhaustiva de ingredientes, cantidades, macronutrientes, micronutrientes y alérgenos]

Analiza con el máximo nivel de detalle toda la comida visible en la imagen como si fuera una única comida completa que voy a consumir.
Contexto adicional: el usuario se encuentra en **${location}**.
⚠️ Es fundamental que no omitas NINGÚN ingrediente, alérgeno ni micronutriente que puedas identificar visualmente o inferir de forma razonable. Si dudas, inclúyelo con tu mejor estimación.

Si la imagen contiene porciones de diferente tamaño o cantidad, asegúrate de estimar correctamente el volumen de cada elemento y reflejarlo en el campo "totalEstimatedWeight".

Tu respuesta debe ser un **único objeto JSON válido** y **NO debe contener texto adicional, explicaciones ni bloques de código**. El idioma de la respuesta debe ser **${language}**.

El formato debe ser exactamente este (no añadas otros campos ni modifiques la estructura):

{
  "name": "Nombre del plato o si no es comida: **No se ha detectado comida**",
  "totalEstimatedWeight": número,
  "ingredients": [["Ingrediente 1", gramos], ["Ingrediente 2", gramos], ...],
  "kcal": número,
  "carbohydrates": número,
  "proteins": número,
  "fats": número,
  "fiber": número,
  "sugar": número,
  "salt": número,
  "key_Micronutrients": [["Micronutriente1", número], ["Micronutriente2", número],...],
  "allergens": ["alérgeno1", "alérgeno2",...]
}

**Instrucciones clave:**
- Si NO identificas micronutrientes o alérgenos, asigna un array vacío ([]).
- **Incluye TODOS los micronutrientes detectados, sin límite de cantidad.**
- Todos los valores deben estar expresados en gramos, excepto:
   - ""kcal"" en kilocalorías
   - ""key_Micronutrients"" en miligramos
   - ""totalEstimatedWeight"" en gramos
- NO incluyas unidades en los valores (sólo números).
- NO incluyas texto fuera del JSON.
- Si tienes dudas sobre cantidades, estima de forma conservadora.

Ejemplo de "key_Micronutrients":
[["Vitamina C", 50], ["Calcio", 200], ["Hierro", 15]]

Ejemplo de "allergens":
["Gluten", "Lactosa"]

⚠️ Revisa cuidadosamente que la lista de ingredientes coincida con TODO lo visible en la imagen.`;
}

/**
 * Envía una imagen base64 a OpenAI Vision para su análisis nutricional.
 * Reintenta si hay errores de red o 5xx.
 * @param idPost ID interno del usuario.
 * @param uid UID Firebase del usuario.
 * @param base64Image Imagen en base64 (data URL).
 * @param prompt Prompt de instrucciones.
 * @returns Objeto {data, idPost, image} o null si falla.
 */
export async function sendImageToNiji(
    idPost: any,
    uid: any,
    base64Image: string,
    prompt: string = getDefaultPrompt(getLanguageLabel(getUserLanguage()) || 'español')
): Promise<any | null> {
    if (!isSupportedImage(base64Image)) {
        console.error("❌ Imagen no compatible");
        return null;
    }

    const maxRetries = 1;
    const retryDelay = 3000; // 3s entre intentos

    let attempt = 0;

    while (attempt <= maxRetries) {
        attempt++;
        logEvent(`📡 Enviando imagen a OpenAI (intento ${attempt}/${maxRetries + 1})...`);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
            console.warn("⏳ Timeout alcanzado, abortando...");
            controller.abort();
        }, 30000);

        try {
            await incrementTokens(uid);
            const response = await openai.chat.completions.create(
                {
                    model: CalooAdminConfig.nijiConfig.openaiModel || "gpt-4o",
                    messages: [
                        {
                            role: "user",
                            content: [
                                { type: "text", text: await buildPromptWithLocation(prompt) },
                                { type: "image_url", image_url: { url: base64Image } },
                            ],
                        },
                    ],
                    max_tokens: 1000,
                },
                {
                    signal: controller.signal,
                }
            );

            clearTimeout(timeoutId);
            const textContent = response.choices?.[0]?.message?.content || "";
            logEvent(`📥 Respuesta OpenAI: ${JSON.stringify(textContent)}`);

            const jsonData = cleanAndParseJSON(textContent);
            if (!jsonData || !jsonData?.name || isNoFoodDetected(jsonData.name)) {
                logEvent(`⚠️ No se detectó comida válida (OpenAI): ${JSON.stringify(jsonData)}`);
                return null;
            }
            // if (
            //     jsonData?.name.trim() === "" ||
            //     ["null", "undefined", "No se ha detectado comida", "**No se ha detectado comida**"].includes(jsonData?.name.trim())
            // ) {
            //     logEvent(`⚠️ No se detectó contenido válido: ${jsonData}`);
            //     return null;
            // }

            const foodTracked = await addNewFoodTracking(idPost, {
                fbId: uid,
                availableTokens: 0,
                result: JSON.stringify({ ...jsonData, model: 'openai', source: 'image' }),
                tokens: response?.usage?.total_tokens ?? 0,
            });

            if (!foodTracked) {
                logEvent(`⚠️ Error creando el registro de tracking por imagen openai para idPost: ${idPost}`);
            }

            const imageSaved = appStore.get("settingsCaloo")?.savePhotos === false ? '/assets/img/cards/notImage.svg' : await saveImageToAppStorage(base64Image, `${foodTracked && foodTracked.idPost && foodTracked.idPost !== undefined ? foodTracked.idPost : Date.now()}.jpg`);
            logEvent(`✅ Imagen guardada correctamente: ${imageSaved}`);
            return {
                data: JSON.stringify(jsonData),
                idPost: foodTracked.idPost,
                image: imageSaved || "",
            };

        } catch (err: any) {
            clearTimeout(timeoutId);

            // Si es un abort, no reintentar
            if (err.name === "AbortError") {
                logEvent("❌ Petición abortada por timeout.");
                return null;
            }

            // Errores con response HTTP
            if (err?.response) {
                const status = err.response.status;
                logEvent(`❌ Error HTTP ${status}: ${err.response.data}`);

                if (status >= 400 && status < 500) {
                    // Cliente: no reintentar
                    return null;
                }

                if (status >= 500) {
                    // Servidor: reintentamos si quedan intentos
                    if (attempt <= maxRetries) {
                        logEvent(`🔁 Reintentando petición (intento ${attempt}/${maxRetries})...`);
                        await new Promise((r) => setTimeout(r, retryDelay));
                        continue;
                    } else {
                        logEvent("❌ Máximo de reintentos alcanzado.");
                        return null;
                    }
                }
            }

            // Error de red o desconocido
            logEvent(`❌ Error inesperado: ${err.message || err}`);
            if (attempt <= maxRetries) {
                logEvent(`🔁 Reintentando petición (intento ${attempt}/${maxRetries})...`);
                await new Promise((r) => setTimeout(r, retryDelay));
                continue;
            }
            return null;
        }
    }

    return null;
}
/**
 * Verifica si una imagen base64 tiene un formato soportado.
 * @param base64Image Imagen en base64 (data URL).
 * @returns true si es un formato soportado.
 */
function isSupportedImage(base64Image: string): boolean {
    return (
        base64Image.startsWith('data:image/jpeg;base64,') ||
        base64Image.startsWith('data:image/png;base64,') ||
        base64Image.startsWith('data:image/gif;base64,') ||
        base64Image.startsWith('data:image/webp;base64,')
    );
}

export async function addNewFoodTracking(userIdPost: any, data: any) {
    try {
        const response = await axios.post(`${BASE_URL}/niji/set/foodtracking/${userIdPost}/`, { data }, {
            headers: headers,
        });

        // console.log('->Response: ', JSON.stringify(response.data));
        return response.data;

    } catch (error) {
        logEvent(`* Error addNewFoodTracking: ${error}`);
        return error;
    }
};


/**
 * Envía imagen a Gemini Vision y devuelve el JSON nutricional.
 */
export async function sendImageToGemini(
    idPost: string,
    uid: string,
    base64Image: string,
    prompt: string = getDefaultPrompt(getLanguageLabel(getUserLanguage()) || 'español')
): Promise<any | null> {
    if (!isSupportedImage(base64Image)) {
        console.error("❌ Imagen no compatible");
        return null;
    }

    const maxRetries = 1;
    const retryDelay = 3000;
    let attempt = 0;

    while (attempt <= maxRetries) {
        attempt++;
        logEvent(`📡 Enviando imagen a Gemini (intento ${attempt}/${maxRetries + 1})...`);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
            console.warn("⏳ Timeout alcanzado, abortando...");
            controller.abort();
        }, 30000);

        try {
            // Crear modelo
            await incrementTokens(uid);
            const model = genAI.getGenerativeModel({ model: CalooAdminConfig.nijiConfig.geminiModel || "gemini-1.5-pro-latest" });

            // Formatear imagen
            const parts = [
                { text: await buildPromptWithLocation(prompt) },
                {
                    inlineData: {
                        mimeType: "image/jpeg",
                        data: base64Image.replace(/^data:image\/\w+;base64,/, "")
                    }
                }
            ];


            const result = await model.generateContent({ contents: [{ role: "user", parts }] }, { signal: controller.signal });

            clearTimeout(timeoutId);

            const textContent = result?.response?.candidates?.[0]?.content?.parts?.[0]?.text || "";
            logEvent(`📥 Respuesta Gemini: ${JSON.stringify(textContent)}`);

            const jsonData = cleanAndParseJSON(textContent);
            console.log("🔍 JSON limpio:", jsonData);
            if (!jsonData || !jsonData?.name || isNoFoodDetected(jsonData.name)) {
                logEvent(`⚠️ No se detectó comida válida (Gemini): ${JSON.stringify(jsonData)}`);
                return null;
            }

            // if (
            //     jsonData?.name.trim() === "" ||
            //     ["null", "undefined", "No se ha detectado comida", "**No se ha detectado comida**"].includes(jsonData?.name.trim())
            // ) {
            //     logEvent(`⚠️ No se detectó contenido válido: ${jsonData}`);
            //     return null;
            // }

            // Registrar tracking
            const foodTracked = await addNewFoodTracking(idPost, {
                fbId: uid,
                availableTokens: 0,
                result: JSON.stringify({ ...jsonData, model: 'gemini', source: 'image' }),
            });

            if (!foodTracked) {
                logEvent(`⚠️ Error creando el registro de tracking por imagen gemini para idPost: ${idPost}`);
            }
            const imageSaved = appStore.get("settingsCaloo")?.savePhotos === false ? '/assets/img/cards/notImage.svg' : await saveImageToAppStorage(base64Image, `${foodTracked && foodTracked.idPost && foodTracked.idPost !== undefined ? foodTracked.idPost : Date.now()}.jpg`);
            logEvent(`✅ Imagen guardada correctamente: ${imageSaved}`);

            return {
                data: JSON.stringify(jsonData),
                idPost: foodTracked.idPost,
                image: imageSaved || "",
            };

        } catch (err: any) {
            clearTimeout(timeoutId);

            if (err.name === "AbortError") {
                logEvent("❌ Petición abortada por timeout.");
                return null;
            }

            logEvent(`❌ Error inesperado: ${err.message || err}`);
            if (attempt <= maxRetries) {
                logEvent(`🔁 Reintentando petición (intento ${attempt}/${maxRetries})...`);
                await new Promise((r) => setTimeout(r, retryDelay));
                continue;
            }
            return null;
        }
    }

    return null;
}

/**
 * Procesa la respuesta de Gemini y la convierte en JSON.
 */
export function processGeminiResponse(rawText: string): any | null {
    try {
        if (!rawText || rawText.trim() === "") {
            console.warn("⚠️ Respuesta vacía");
            return null;
        }

        // Si está en bloque de código, limpiamos
        let cleaned = rawText.trim();

        // Elimina ```json y ```
        if (cleaned.startsWith("```json")) {
            cleaned = cleaned.replace(/^```json/, "").trim();
        }
        if (cleaned.endsWith("```")) {
            cleaned = cleaned.slice(0, -3).trim();
        }

        // Log opcional para depuración
        console.log("🔍 JSON limpio:", cleaned);
        const parsed = safeJSONParser(cleaned);
        return parsed;
    } catch (err) {
        console.error("❌ Error procesando respuesta Gemini:", err, rawText);
        return null;
    }
}

export function cleanAndParseJSON(rawText: string): any | null {
    try {
        if (!rawText || typeof rawText !== "string") return null;

        let cleaned = rawText.trim();

        // Limpia bloque de código tipo ```json
        if (cleaned.startsWith("```json")) {
            cleaned = cleaned.replace(/^```json/, "").trim();
        } else if (cleaned.startsWith("```")) {
            cleaned = cleaned.replace(/^```/, "").trim();
        }

        if (cleaned.endsWith("```")) {
            cleaned = cleaned.slice(0, -3).trim();
        }

        let parsed = safeJSONParser(cleaned);
        if (parsed) return parsed;
        // 🔁 Solo si falla, intentamos sanear escapando \n y \t
        cleaned = cleaned.replace(/\n/g, "\\n").replace(/\t/g, "\\t");
        return safeJSONParser(cleaned);
    } catch (err) {
        console.warn("❌ Error al limpiar o parsear JSON:", err, rawText);
        return null;
    }
}


/**
 * Devuelve true si el nombre indica que no se ha detectado comida, en cualquiera de los 7 idiomas soportados.
 */
export function isNoFoodDetected(nameRaw: string): boolean {
    if (!nameRaw || typeof nameRaw !== "string") return true;

    const name = nameRaw.trim().toLowerCase();

    // Frases exactas que pueden llegar del modelo
    const knownPatterns = [
        // Español
        "no se ha detectado comida",
        "**no se ha detectado comida**",
        // Catalán
        "no s'ha detectat menjar",
        "**no s'ha detectat menjar**",
        // Gallego
        "non se detectou comida",
        "**non se detectou comida**",
        // Portugués
        "não foi detectado alimento",
        "**não foi detectado alimento**",
        // Francés
        "aucune nourriture détectée",
        "**aucune nourriture détectée**",
        // Italiano
        "nessun cibo rilevat*",
        "**nessun cibo rilevato**",
        // Inglés
        "no food detected",
        "**no food detected**"
    ];

    if (knownPatterns.includes(name)) return true;

    // Palabras clave por si devuelve algo como "**No food detected**"
    const keywords = [
        "no food",
        "no comida",
        "No se ha detectado",
        "no se detectó",
        "no s'ha detectat",
        "non se detectou",
        "não foi detectado",
        "aucune nourriture",
        "nessun cibo"
    ];

    for (const kw of keywords) {
        if (name.includes(kw)) return true;
    }

    return false;
}

export async function sendRecipeDescriptionToNiji(
    idPost: any,
    uid: any,
    prompt: string
): Promise<any | null> {
    const maxRetries = 1;
    const retryDelay = 3000;
    let attempt = 0;

    if (!prompt || prompt.trim() === "") {
        logEvent("❌ Prompt vacío, no se enviará a OpenAI.");
        return null;
    }

    while (attempt <= maxRetries) {
        attempt++;
        logEvent(`📡 Enviando prompt texto a OpenAI (intento ${attempt}/${maxRetries + 1})...`);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
            console.warn("⏳ Timeout alcanzado, abortando...");
            controller.abort();
        }, 30000);

        try {
            const response = await openai.chat.completions.create(
                {
                    model: CalooAdminConfig?.nijiConfig?.textModel || "gpt-4o",
                    messages: [
                        {
                            role: "user",
                            content: [
                                { type: "text", text: await buildPromptWithLocation(getDefaultPrompt(getLanguageLabel(getUserLanguage()) || 'español', 'audio')) },
                                { type: "text", text: prompt },
                            ],
                        },
                    ],
                    max_tokens: 1000,
                },
                {
                    signal: controller.signal,
                }
            );

            clearTimeout(timeoutId);
            console.log("📥 Respuesta OpenAI:", response);
            await incrementTokensText(uid, response?.usage?.total_tokens ?? 0);
            const textContent = response.choices?.[0]?.message?.content || "";
            const jsonData = safeJSONParser(textContent);
            if (!jsonData) {
                logEvent(`❌ No se pudo parsear el JSON audio de OpenAI: ${textContent}`);
                return null;
            }
            if (!jsonData?.name || isNoFoodDetected(jsonData?.name)) {
                logEvent(`⚠️ No se detectó contenido válid en el audio: ${JSON.stringify(jsonData)}`);
                return null;
            }

            const foodTracked = await addNewFoodTracking(idPost, {
                fbId: uid,
                availableTokens: 0,
                result: JSON.stringify({ ...jsonData, model: 'openai', source: 'text' }),
                tokens: response?.usage?.total_tokens ?? 0,
            });

            if (!foodTracked) {
                logEvent(`⚠️ Error creando el registro de tracking por texto para idPost: ${idPost}`);
            }

            return {
                data: jsonData,
            };

        } catch (err: any) {
            clearTimeout(timeoutId);

            if (err.name === "AbortError") {
                logEvent("❌ Petición abortada por timeout.");
                return null;
            }

            if (err?.response) {
                const status = err.response.status;
                logEvent(`❌ Error HTTP ${status}: ${err.response.data}`);

                if (status >= 400 && status < 500) {
                    return null;
                }

                if (status >= 500 && attempt <= maxRetries) {
                    logEvent(`🔁 Reintentando petición (intento ${attempt}/${maxRetries})...`);
                    await new Promise((r) => setTimeout(r, retryDelay));
                    continue;
                }

                logEvent("❌ Máximo de reintentos alcanzado.");
                return null;
            }

            logEvent(`❌ Error inesperado: ${err.message || err}`);
            if (attempt <= maxRetries) {
                logEvent(`🔁 Reintentando petición (intento ${attempt}/${maxRetries})...`);
                await new Promise((r) => setTimeout(r, retryDelay));
                continue;
            }

            return null;
        }
    }

    return null;
}

export async function sendRecipeCorrectionToNiji(
    idPost: any,
    uid: any,
    originalJSON: object,
    userComment: string
): Promise<any | null> {
    const maxRetries = 1;
    const retryDelay = 3000;
    let attempt = 0;

    if (!userComment || userComment.trim() === "") {
        logEvent("❌ Comentario vacío, no se enviará a OpenAI.");
        return null;
    }

    while (attempt <= maxRetries) {
        attempt++;
        logEvent(`📡 Enviando prompt de corrección a OpenAI (intento ${attempt}/${maxRetries + 1})...`);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
            console.warn("⏳ Timeout alcanzado, abortando...");
            controller.abort();
        }, 30000);

        try {

            const correctionPrompt = (await buildPromptWithLocation(getDefaultPrompt(getLanguageLabel(getUserLanguage()) || 'español', 'edit'))).replace('**${userFeedback}**', userComment)
                .replace('**${originalJSON}**', JSON.stringify(originalJSON, null, 2));;

            const response = await openai.chat.completions.create(
                {
                    model: CalooAdminConfig?.nijiConfig?.textModel || "gpt-4o",
                    messages: [
                        {
                            role: "user",
                            content: [{ type: "text", text: correctionPrompt }],
                        },
                    ],
                    max_tokens: 1000,
                },
                {
                    signal: controller.signal,
                }
            );

            clearTimeout(timeoutId);
            console.log("📥 Respuesta OpenAI:", response);
            await incrementTokensText(uid, response?.usage?.total_tokens ?? 0);

            const textContent = response.choices?.[0]?.message?.content || "";
            const jsonData = safeJSONParser(textContent);

            if (!jsonData) {
                logEvent(`❌ No se pudo parsear el JSON corregido de OpenAI: ${textContent}`);
                return null;
            }

            if (!jsonData?.name || isNoFoodDetected(jsonData?.name)) {
                logEvent(`⚠️ No se detectó contenido válido tras corrección: ${JSON.stringify(jsonData)}`);
                return null;
            }

            return {
                data: jsonData,
            };

        } catch (err: any) {
            clearTimeout(timeoutId);

            if (err.name === "AbortError") {
                logEvent("❌ Petición abortada por timeout.");
                return null;
            }

            if (err?.response) {
                const status = err.response.status;
                logEvent(`❌ Error HTTP ${status}: ${err.response.data}`);

                if (status >= 400 && status < 500) {
                    return null;
                }

                if (status >= 500 && attempt <= maxRetries) {
                    logEvent(`🔁 Reintentando petición (intento ${attempt}/${maxRetries})...`);
                    await new Promise((r) => setTimeout(r, retryDelay));
                    continue;
                }

                logEvent("❌ Máximo de reintentos alcanzado.");
                return null;
            }

            logEvent(`❌ Error inesperado: ${err.message || err}`);
            if (attempt <= maxRetries) {
                logEvent(`🔁 Reintentando petición (intento ${attempt}/${maxRetries})...`);
                await new Promise((r) => setTimeout(r, retryDelay));
                continue;
            }

            return null;
        }
    }

    return null;
}